import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';

// Components 

import Box from '@mui/material/Box';
import { TextField } from '@mui/material';
import Button from '../../../general/inputs/Button';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { setNotificationMessage } from '../../../../redux/features/general/notification';
import { setIsAgreementPopupOpened, setIsSecurityPinPopupOpened } from '../../../../redux/features/general/navigation';
import { updateDoctorPreferences } from '../../../../routes/doctor/patients';
import CancelButton from '../../../general/inputs/CancelButton';


interface Props { 
    isLockScreenContext?: boolean; // New prop to indicate if opened from lock screen
    onClose?: (pinSetSuccessfully: boolean) => void; // New prop for custom close handler
}; 

const SecurityPin: React.FC<Props> = ({ isLockScreenContext, onClose }) => {

    const dispatch = useAppDispatch(); 
    const { t } = useTranslation(); 
    const navigate = useNavigate(); 
    const isMobile = useMediaQuery('(max-width: 768px)');
    const location = useLocation(); // Get current location

    const colors = useAppSelector(state => state.theme.colors); 

    const [error, setError] = useState(false) as any; 

    const [pin0, setPin0] = useState(''); 
    const [pin1, setPin1] = useState(''); 
    const [pin2, setPin2] = useState(''); 
    const [pin3, setPin3] = useState(''); 

    const pin0Ref = useRef() as any; 
    const pin1Ref = useRef() as any; 
    const pin2Ref = useRef() as any;  
    const pin3Ref = useRef() as any; 

    // -- 

    const textFieldStyle = { 
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-evenly', 
        width: isMobile ? 50 : 60, 
        height: isMobile ? 50 : 60, 
        fontSize: 18, 
        margin: '0 4px',
        alignSelf: 'center',
        color: colors?.text,
        borderBottomColor: 'black',
        borderColor: 'rgb(10,197,207)',
        borderStyle: 'solid',
        borderRadius: 3, 
        borderWidth: 1, 
    }; 

    // -- 

    const handleSecurityPin = (number: any, order: number) => { 

        const reg = new RegExp('^[0-9\-/]+$'); 

        if (((number.length > 1) || !reg.test(number)) && number) { 
            return; 
        }; 

        order === 0 && setPin0(number); 
        order === 1 && setPin1(number); 
        order === 2 && setPin2(number); 
        order === 3 && setPin3(number); 

        if (number) { 
    
            order === 0 && pin1Ref.current.focus(); 
            order === 1 && pin2Ref.current.focus(); 
            order === 2 && pin3Ref.current.focus(); 

        } else  {

            order === 1 && pin0Ref.current.focus(); 
            order === 2 && pin1Ref.current.focus(); 
            order === 3 && pin2Ref.current.focus(); 

        };

        setError(false); 

    }; 

    // -- 

    const handleVerifySecurityPin = async () => { 

        const updatedPreferences = { 
            securityPin: `${pin0}${pin1}${pin2}${pin3}`
        }; 

        const res = await updateDoctorPreferences(updatedPreferences); 

        if (res?.data?.status === 200) { 

            if (res.data.doctorPreferences.securityPin) { 

                dispatch(setNotificationMessage(t('securityPinUpdated'))); 
                dispatch(setIsSecurityPinPopupOpened(false)); 
                if (onClose) { // Use custom onClose if provided (e.g., from LockScreenContent)
                    onClose(true); // Indicate PIN was set successfully
                }

            }; 

        } else if (res?.data?.status === 403) { 

            setError(true); 

        } else { 

            navigate('/login'); 

        }; 

    }; 

    const handleQuit = () => { 
        dispatch(setIsSecurityPinPopupOpened(false)); 
        if (onClose) { // Use the custom onClose if provided
            onClose(false); // Indicate PIN was NOT set successfully
        } else if (isLockScreenContext && location.pathname === '/lock/screen') {
            // Fallback: if it's lock screen context and no custom onClose, redirect to login
            // This path should ideally not be hit with the new LockScreenContent logic
            navigate('/login');
        }
    }; 

    // -- 

    useEffect(() => { 

        if (pin0 && pin1 && pin2 && pin3) { 

            handleVerifySecurityPin(); 

        }; 

    },[pin0, pin1, pin2, pin3]); 
    
    // -- 

    return (

        <Box 
            onClick={handleQuit}
            sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                width: '100vw',
                height: '100vh', 
                backgroundColor: colors?.popup, 
                overflow: 'hidden', 
                position: 'absolute', 
                zIndex: 100000000,
        }}>

            <Box 
                onClick={(e) => e.stopPropagation()}
                sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                maxWidth: isMobile ? '90vw' : '450px',
                padding: isMobile ? 2 : 0,
                borderRadius: 3, 
                backgroundColor: colors?.container,
                position: 'relative',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
            }}>

                <Box sx={{ 
                    color: colors?.text,
                    marginTop: 4,
                    fontSize: isMobile ? 18 : 20, 
                    fontWeight: 'bold'
                }}>
                    {t('updatePin')}
                </Box>

                <Box sx={{ 
                    color: colors?.text,
                    marginTop: 1,
                    fontSize: 12, 
                    width: '80%',
                    marginBottom: 1, 
                    textAlign: 'center'
                }}>
                    {t('pinInfo')}
                </Box>

                {error && 
                <Box sx={{ 
                    marginTop: 1,
                    fontSize: 15,
                    color: colors?.red 
                }}>
                    {t('invalidPin')}
                </Box>}

                <Box
                    sx={{ 
                        display: 'flex', 
                        flexDirection: 'row',
                        alignItems: 'center',
                        width: '100%',
                        justifyContent: 'center', 
                        marginTop: 3, 
                        marginBottom: 4, 
                    }}
                >

                    <TextField 
                        sx={textFieldStyle} 
                        inputProps={{ 
                            style: { 
                                textAlign: 'center',
                                height: '100%', 
                                width: '100%', 
                                fontSize: 20, 
                                textDecoration: 'none',
                                color: colors?.text
                            }
                        }}
                        InputProps={{ disableUnderline: true }}
                        onChange={(e) => handleSecurityPin(e.target.value, 0)}
                        autoComplete='off'
                        id="pin-0" 
                        value={pin0}
                        variant="standard" 
                        autoFocus
                        type='text'
                        inputRef={pin0Ref}
                    />

                    <TextField 
                        sx={textFieldStyle} 
                        inputProps={{ 
                            style: { 
                                textAlign: 'center',
                                height: '100%', 
                                width: '100%', 
                                fontSize: 20, 
                                textDecoration: 'none',
                                color: colors?.text
                            }
                        }}
                        InputProps={{ disableUnderline: true }}
                        onChange={(e) => handleSecurityPin(e.target.value, 1)}
                        autoComplete='off'
                        id="pin-1" 
                        value={pin1}
                        variant="standard" 
                        type='text'
                        inputRef={pin1Ref}
                    />

                    <TextField 
                        sx={textFieldStyle} 
                        inputProps={{ 
                            style: { 
                                textAlign: 'center',
                                height: '100%', 
                                width: '100%', 
                                fontSize: 20, 
                                textDecoration: 'none',
                                color: colors?.text
                            }
                        }}
                        InputProps={{ disableUnderline: true }}
                        onChange={(e) => handleSecurityPin(e.target.value, 2)}
                        autoComplete='off'
                        id="pin-2" 
                        value={pin2}
                        variant="standard" 
                        type='text'
                        inputRef={pin2Ref}
                    />

                    <TextField 
                        sx={textFieldStyle} 
                        inputProps={{ 
                            style: { 
                                textAlign: 'center',
                                height: '100%', 
                                width: '100%', 
                                fontSize: 20, 
                                textDecoration: 'none',
                                color: colors?.text
                            }
                        }}
                        InputProps={{ disableUnderline: true }}
                        onChange={(e) => handleSecurityPin(e.target.value, 3)}
                        autoComplete='off'
                        id="pin-3" 
                        value={pin3}
                        variant="standard" 
                        type='text'
                        inputRef={pin3Ref}
                    />


                </Box>

                <Box
                    component="img"
                    sx={{
                        height: isMobile ? 60 : 70,
                        width: isMobile ? 60 : 70,
                        margin: '16px 0'
                    }}
                    alt="Login Background"
                    src={require('../../../../assets/icons/auth/moonlight.png')}
                />

                <CancelButton 
                    handler={handleQuit}
                />

            </Box>

        </Box>

  );

};

export default SecurityPin;